import { Component, OnInit } from '@angular/core';
import { ApiService } from '../services/api.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { FormControl } from '@angular/forms';
import { exportCustomerTransferReportToPDF } from './customer-transfer-report-pdf';

export interface CustomerTransfer {
  customerId: number;
  customerName: string;
  customerPhone: number;
  previousAreaCode: number;
  previousAreaName: string;
  newAreaCode: number;
  newAreaName: string;
  transferredBy: string;
  transferredAt: string;
}

export interface ColumnConfig {
  key: keyof CustomerTransfer;
  label: string;
  visible: boolean;
}

@Component({
  selector: 'app-customer-transfer-report',
  templateUrl: './customer-transfer-report.component.html',
  styleUrls: ['../billing-dashboard/billing-dashboard.component.scss', './customer-transfer-report.component.scss']
})
export class CustomerTransferReportComponent implements OnInit {
  loading = false;
  transfers: CustomerTransfer[] = [];
  filteredTransfers: CustomerTransfer[] = [];
  totalTransfers = 0;

  currentCycleStart = '';
  currentCycleEnd = '';
  searchTerm = '';
  sortColumn: string = '';
  sortDirection: 'asc' | 'desc' = 'asc';

  availableCycles: { label: string; startDate: string; endDate: string }[] = [];
  selectedCycleControl = new FormControl('');
  showCycleSelector = false;

  allColumns: ColumnConfig[] = [
    { key: 'customerId', label: 'Customer ID', visible: true },
    { key: 'customerName', label: 'Customer Name', visible: true },
    { key: 'customerPhone', label: 'Phone', visible: true },
    { key: 'previousAreaName', label: 'From Area', visible: true },
    { key: 'newAreaName', label: 'To Area', visible: true },
    { key: 'transferredAt', label: 'Transfer Date', visible: true },
    { key: 'transferredBy', label: 'Transferred By', visible: true }
  ];

  constructor(
    private apiService: ApiService,
    private snackBar: MatSnackBar
  ) {}

  ngOnInit(): void {
    this.initializeAvailableCycles();
    this.loadData();
  }

  private initializeAvailableCycles(): void {
    const today = new Date();
    const cycles: { label: string; startDate: string; endDate: string }[] = [];
    const seenLabels = new Set<string>();

    for (let i = 0; i < 12; i++) {
      const cycleDate = new Date(today.getFullYear(), today.getMonth() - i, 15);
      let cycleStart = new Date(cycleDate.getFullYear(), cycleDate.getMonth(), 25);
      let cycleEnd = new Date(cycleDate.getFullYear(), cycleDate.getMonth() + 1, 24);

      if (cycleStart > today) {
        cycleStart.setMonth(cycleStart.getMonth() - 1);
        cycleEnd.setMonth(cycleEnd.getMonth() - 1);
      }

      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const label = `${monthNames[cycleStart.getMonth()]} ${cycleStart.getFullYear()} Cycle`;

      if (seenLabels.has(label)) {
        continue;
      }
      seenLabels.add(label);

      cycles.push({
        label,
        startDate: this.formatDate(cycleStart),
        endDate: this.formatDate(cycleEnd)
      });
    }

    this.availableCycles = cycles;

    if (cycles.length > 0) {
      this.selectedCycleControl.setValue(cycles[0].label);
      this.currentCycleStart = cycles[0].startDate;
      this.currentCycleEnd = cycles[0].endDate;
    }
  }

  private formatDate(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  toggleCycleSelector(): void {
    this.showCycleSelector = !this.showCycleSelector;
  }

  onCycleChange(selectedLabel: string): void {
    const selectedCycle = this.availableCycles.find(cycle => cycle.label === selectedLabel);
    if (selectedCycle) {
      this.selectedCycleControl.setValue(selectedLabel);
      this.currentCycleStart = selectedCycle.startDate;
      this.currentCycleEnd = selectedCycle.endDate;
      this.loadData();
    }
  }

  loadData(): void {
    this.loading = true;
    this.apiService.getCustomerTransferReport(this.currentCycleStart, this.currentCycleEnd).subscribe({
      next: (data: any) => {
        this.transfers = data?.transfers || [];
        this.totalTransfers = data?.totalTransfers || 0;
        this.filterCustomers();
        this.loading = false;
      },
      error: (error) => {
        console.error('Error loading transfer report:', error);
        this.loading = false;
        this.snackBar.open('Error loading customer transfer report', 'Close', { duration: 3000 });
      }
    });
  }

  filterCustomers(): void {
    if (!this.searchTerm || this.searchTerm.trim() === '') {
      this.filteredTransfers = [...this.transfers];
    } else {
      const searchLower = this.searchTerm.toLowerCase().trim();
      this.filteredTransfers = this.transfers.filter(t =>
        (t.customerName && t.customerName.toLowerCase().includes(searchLower)) ||
        t.customerId.toString().includes(searchLower) ||
        (t.previousAreaName && t.previousAreaName.toLowerCase().includes(searchLower)) ||
        (t.newAreaName && t.newAreaName.toLowerCase().includes(searchLower)) ||
        (t.transferredBy && t.transferredBy.toLowerCase().includes(searchLower))
      );
    }
    this.applySort();
  }

  sortBy(column: string): void {
    if (this.sortColumn === column) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = column;
      this.sortDirection = 'asc';
    }
    this.applySort();
  }

  private applySort(): void {
    if (!this.sortColumn) {
      return;
    }
    const column = this.sortColumn as keyof CustomerTransfer;
    const direction = this.sortDirection === 'asc' ? 1 : -1;
    this.filteredTransfers = [...this.filteredTransfers].sort((a, b) => {
      const aVal = (a as any)[column];
      const bVal = (b as any)[column];
      if (aVal == null) return 1;
      if (bVal == null) return -1;
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        return aVal.localeCompare(bVal) * direction;
      }
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return (aVal - bVal) * direction;
      }
      return String(aVal).localeCompare(String(bVal)) * direction;
    });
  }

  getSortIcon(column: string): string {
    if (this.sortColumn !== column) {
      return 'unfold_more';
    }
    return this.sortDirection === 'asc' ? 'arrow_upward' : 'arrow_downward';
  }

  formatTransferDate(isoDate: string): string {
    if (!isoDate) {
      return '-';
    }
    const date = new Date(isoDate);
    if (isNaN(date.getTime())) {
      return isoDate;
    }
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day} ${hours}:${minutes}`;
  }

  exportToPDF(): void {
    exportCustomerTransferReportToPDF(
      this.filteredTransfers,
      this.totalTransfers,
      this.currentCycleStart,
      this.currentCycleEnd
    );
  }

  exportToCSV(): void {
    const headers = this.visibleColumns.map(c => c.label).join(',');
    const rows = this.filteredTransfers.map(t =>
      this.visibleColumns.map(c => {
        const value = (t as any)[c.key];
        if (c.key === 'transferredAt') {
          return this.formatTransferDate(value);
        }
        const text = value != null ? String(value).replace(/"/g, '""') : '';
        return `"${text}"`;
      }).join(',')
    );
    const csv = [
      'Customer Transfer Report',
      `Cycle: ${this.currentCycleStart} to ${this.currentCycleEnd}`,
      `Total Transfers: ${this.totalTransfers}`,
      '',
      headers,
      ...rows
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.setAttribute('hidden', '');
    a.setAttribute('href', url);
    a.setAttribute('download', `customer-transfer-report-${this.currentCycleStart}.csv`);
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);

    this.snackBar.open('Report exported successfully', 'Close', { duration: 3000 });
  }

  get visibleColumns(): ColumnConfig[] {
    return this.allColumns.filter(c => c.visible);
  }
}
